import { FieldValues } from 'react-hook-form';

import { GroupSelectOptionsInterface } from '@components/common';

import { IKPISite, ISelectedOnMapSitesByVendor } from '@interfaces/kpi/kpiCommon';
import {
	IDefaultOptions,
	IGroupSelectItem,
	IGroupSelectOptions,
} from '@components/common/GroupSelect/GroupSelect.types';
import {
	IGroupSelectOptions as GroupSelectOptionsInterfaceKpi,
} from '@components/common/GroupSelectKpi/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EVendorsValues, TTechnologiesValues, TVendorsLabels, TVendorsValues } from '@enums/common';
import { EDateSelectionModes, EDynamicDateRange } from '@enums/kpi-api';

const getVendorOptions = ({
	vendor,
	optionsLimenet,
	optionsNokia,
	options5GOran,
}: {
	vendor: { value?: TVendorsValues; name?: TVendorsLabels; isDefault?: boolean };
	optionsLimenet?: IGroupSelectOptions[];
	optionsNokia?: IGroupSelectOptions[];
	options5GOran?: IGroupSelectOptions[];
}) => {
	switch (vendor.value) {
		case EVendorsValues.limenet:
			return optionsLimenet;
		case EVendorsValues.nokia:
			return optionsNokia;
		case EVendorsValues.oran:
			if (!options5GOran) return [];

			return options5GOran;
		default:
			return [];
	}
};

export const changeOptionsByVendor = ({
	vendor,
	optionsLimenet,
	optionsNokia,
	allSelectedSitesOnMap,
	options5GOran,
	technology,
}: {
	vendor?: { value?: TVendorsValues; name?: TVendorsLabels; isDefault?: boolean };
	optionsLimenet?: Array<IGroupSelectOptions>;
	optionsNokia?: Array<IGroupSelectOptions>;
	options5GOran?: Array<IGroupSelectOptions>;
	allSelectedSitesOnMap?: Array<ISelectedOnMapSitesByVendor>;
	technology?: TTechnologiesValues;
}) => {
	if (!vendor?.value) return [];

	const options: Array<IGroupSelectOptions> | undefined = getVendorOptions({
		vendor,
		options5GOran,
		optionsLimenet,
		optionsNokia,
	});

	if (!allSelectedSitesOnMap?.length) return options;

	const selectedSitesOnMapByVendor = allSelectedSitesOnMap?.find(site => site.vendor === vendor?.value);

	const listOfSitesOnMapByTechnology: (string | number)[] | undefined = selectedSitesOnMapByVendor?.sites
		.filter(site => site.technology === technology)
		.map(site => site.siteId);

	return options?.filter(option => listOfSitesOnMapByTechnology?.includes(option.category.name)) || [];
};

export const changeOptionsByVendorKpi = ({
	vendor,
	optionsLimenet,
	optionsNokia,
	allSelectedSitesOnMap,
	technology,
}: {
	vendor?: TSelectOptionType<TVendorsValues>;
	optionsLimenet: GroupSelectOptionsInterfaceKpi[];
	optionsNokia: GroupSelectOptionsInterfaceKpi[];
	allSelectedSitesOnMap?: ISelectedOnMapSitesByVendor[];
	technology?: TTechnologiesValues;
}) => {
	if (!vendor?.value) return [];

	const options = vendor.value === EVendorsValues.limenet
		? optionsLimenet
		: optionsNokia;

	if (!allSelectedSitesOnMap?.length) return options;

	const selectedSitesOnMapByVendor = allSelectedSitesOnMap?.find(site => site.vendor === vendor?.value);

	const listOfSitesOnMapByTechnology: (string | number)[] | undefined = selectedSitesOnMapByVendor?.sites
		.filter(site => site.technology === technology)
		.map(site => site.siteId);

	return options.filter(option => {
		const filteredItems = option.categoryGroups.map(categoryGroup => {
			const filteredItems = categoryGroup.items.filter(item => {
				return listOfSitesOnMapByTechnology?.includes(item.id);
			});

			return {
				...categoryGroup,
				items: filteredItems,
			};
		});

		return {
			...option,
			categoryGroups: filteredItems,
		};
	});
};

export const generateDefaultValues = (values: Array<IGroupSelectOptions>) => {
	const collectDefaultValues =
		(items: Array<IGroupSelectItem>, parentCategoryName: string): Array<IDefaultOptions> => {
			return items.reduce<Array<IDefaultOptions>>((acc, item) => {
				if (item.items && item.items.length > 0) {
					acc.push(...collectDefaultValues(item.items, item.category?.name || parentCategoryName));
				} else {
					acc.push({
						id: item.id || '',
						categoryName: parentCategoryName,
					});
				}

				return acc;
			}, []);
		};

	return values.reduce<Array<IDefaultOptions>>((acc, option) => {
		const defaultOption = collectDefaultValues(option.items, option.category.name);

		return [...acc, ...defaultOption];
	}, []);
};

export const prepareSiteWithCells = (
	site: IKPISite,
	options?: GroupSelectOptionsInterface[],
): IKPISite => {
	const matchedOption = options?.find(option => option.category.name === site.siteId);

	const cellIds = matchedOption?.items
		.map(item => item.id)
		.filter((id): id is string => typeof id === 'string');

	return {
		...site,
		cellIds: cellIds?.length ? cellIds : undefined,
	};
};

export const get5GTechnologyLabel = (isOranVendor: boolean) => {
	return isOranVendor ? 'node' : 'site';
};

export const getDateSelectionParameters = (
	{
		isOranVendor,
		values,
		dateFromUTC,
		dateToUTC,
	}: {
		isOranVendor: boolean;
		values: FieldValues;
		dateFromUTC?: number | Date | null;
		dateToUTC?: number | Date | null;
	},
) => {
	if (isOranVendor) {
		if (values.dateSelectionMode === EDateSelectionModes.static) {
			return {
				dateSelectionMode: values.dateSelectionMode,
				from: dateFromUTC,
				to: dateToUTC,
			};
		}

		return {
			dateSelectionMode: values.dateSelectionMode,
			dynamicDateRange: values.dynamicDateRange?.value,
			...(values.startDateOffset !== undefined && values.dynamicDateRange?.value === EDynamicDateRange.custom
				? {
					startDateOffset: values.startDateOffset?.value,
				}
				: {}),
		};
	}

	return {
		from: dateFromUTC,
		to: dateToUTC,
	};
};
